/**
 * Keeping instances and definitions apart, and in step where D19 says so:
 *
 *   - **The next instance is re-copied whenever its definition changes**
 *     (D19), and follows a moved part. An automatically chosen next is
 *     re-chosen when the rundown order no longer leads to it (Sofie's
 *     `ensureNextPartIsValid`); a next that became unplayable is replaced.
 *   - **The current instance never changes on a definition edit** — except by
 *     `syncCurrentInstance`, which copies only the live-editable fields
 *     (`LIVE_EDITABLE_PART_FIELDS`, and piece `name`/`params`) after the
 *     operator confirmed "Update on air?".
 *   - **A deleted definition leaves its instance playing, orphaned**
 *     (`orphaned: 'deleted'`), whether current, next or previous (D19:
 *     "the instance continues as orphaned").
 *
 * Also here: the device playback reports (`onPlaybackStarted/Stopped`), which
 * record the as-played times on the instances (PLAN §5.2: "Instances *are*
 * the as-run record") and fire their as-run events once.
 */
import type { PartFields } from '@acp/domain/fields.js';
import { buildContext, commandEventId, type Draft, emit, emitOnce, reject, type Rejection } from './draft.js';
import type { EnginePartInstance, EnginePieceInstance } from './instances.js';
import { refreshNextAfterCurrentChange } from './operations.js';
import { definitionOf, refreshNext } from './placement.js';
import { autoSelectNext, runAutoNext, setNextFromPart, withInstance } from './playout.js';
import { selectNextPart } from './selectNext.js';

/** Part fields an on-air instance may take from its edited definition (D19's "live-editable fields"). */
export const LIVE_EDITABLE_PART_FIELDS = ['title', 'expectedDuration', 'autoNext', 'autoNextOverlap', 'untimed', 'script', 'notes'] as const;

type Which = 'current' | 'next' | 'previous';

function orphanIfDeleted(draft: Draft, which: Which): void {
  const instance = draft.state[which];
  if (instance === null || instance.orphaned !== null || draft.env.model.partById.has(instance.partId)) return;
  draft.state = withInstance(draft.state, which, { ...instance, orphaned: 'deleted' });
  emitOnce(draft, {
    type: 'orphaned',
    id: `orphaned:${instance.instanceId}`,
    time: draft.env.now,
    partInstanceId: instance.instanceId,
    which,
    reason: 'deleted',
  });
}

function fingerprint(instance: EnginePartInstance): string {
  return JSON.stringify([instance.part, instance.segmentId, instance.pieces.map((p) => [p.instanceId, p.piece, p.infinite?.infiniteInstanceId ?? null])]);
}

/** D19 for the next instance: re-choose, replace or re-copy it. */
function syncNext(draft: Draft): void {
  const ctx = buildContext(draft);
  const { next, current, nextSetManually, queuedSegmentId } = draft.state;
  if (ctx === null) return;
  if (next === null) {
    autoSelectNext(draft);
    return;
  }
  const def = definitionOf(draft.env.model, next);
  if (def !== null && !def.playable) {
    autoSelectNext(draft);
    return;
  }
  if (!nextSetManually && next.orphaned === null) {
    const selected = selectNextPart(draft.env.model, current, queuedSegmentId);
    if (selected === null || selected.part.partId !== next.partId) {
      if (selected === null) autoSelectNext(draft);
      else setNextFromPart(draft, selected.part, false, selected.consumesQueuedSegmentId);
      return;
    }
  }
  const refreshed = refreshNext(ctx, next, current);
  if (fingerprint(refreshed) === fingerprint(next)) return;
  draft.state = { ...draft.state, next: refreshed };
  emit(draft, { type: 'next-synced', id: commandEventId(draft, 'next-synced'), time: draft.env.now, partInstanceId: refreshed.instanceId });
}

/** Bring the instances in line with a changed model (module docstring). */
export function syncDefinitions(draft: Draft): Rejection | null {
  orphanIfDeleted(draft, 'current');
  orphanIfDeleted(draft, 'previous');
  orphanIfDeleted(draft, 'next');
  if (draft.state.activation !== null) syncNext(draft);
  return null;
}

function livePartFields(instance: PartFields, def: PartFields): PartFields {
  const out: PartFields = { ...instance };
  out.title = def.title;
  out.expectedDuration = def.expectedDuration;
  out.autoNext = def.autoNext;
  out.autoNextOverlap = def.autoNextOverlap;
  out.untimed = def.untimed;
  out.script = def.script;
  out.notes = def.notes;
  return out;
}

/** A piece instance with its definition's live fields (`name`, `params`), when the definition still exists. */
function livePiece(draft: Draft, piece: EnginePieceInstance): EnginePieceInstance {
  if (piece.dynamicallyInserted !== null || piece.startPartId === null) return piece;
  const def = draft.env.model.partById.get(piece.startPartId)?.pieces.find((p) => p.pieceId === piece.pieceId);
  if (def === undefined) return piece;
  return { ...piece, piece: { ...piece.piece, name: def.fields.name, params: def.fields.params } };
}

/** "Update on air?" confirmed (D19): the live-editable fields into the on-air instance. */
export function syncCurrentInstance(draft: Draft): Rejection | null {
  const { current } = draft.state;
  if (current === null) return reject('no-current-part', 'nothing is on air', null);
  const def = definitionOf(draft.env.model, current);
  if (def === null) return reject('not-live-editable', `the on-air instance has no definition (${current.orphaned ?? 'missing'})`, null);
  draft.state = {
    ...draft.state,
    current: { ...current, part: livePartFields(current.part, def.fields), pieces: current.pieces.map((p) => livePiece(draft, p)) },
  };
  emit(draft, { type: 'current-synced', id: commandEventId(draft, 'current-synced'), time: draft.env.now, partInstanceId: current.instanceId });
  refreshNextAfterCurrentChange(draft);
  return null;
}

type Found =
  | { readonly kind: 'part'; readonly which: Which; readonly instance: EnginePartInstance }
  | { readonly kind: 'piece'; readonly which: Which; readonly instance: EnginePartInstance; readonly piece: EnginePieceInstance };

const WHICH: readonly Which[] = ['current', 'next', 'previous'];

/** A part instance by id, or a piece instance by its instance id or its infinite chain's id (current first). */
function find(draft: Draft, id: string): Found | null {
  for (const which of WHICH) {
    const instance = draft.state[which];
    if (instance?.instanceId === id) return { kind: 'part', which, instance };
  }
  for (const which of WHICH) {
    const instance = draft.state[which];
    const piece = instance?.pieces.find((p) => p.instanceId === id || p.infinite?.infiniteInstanceId === id);
    if (instance !== null && piece !== undefined) return { kind: 'piece', which, instance, piece };
  }
  return null;
}

function replacePieces(draft: Draft, id: string, change: (piece: EnginePieceInstance) => EnginePieceInstance): void {
  for (const which of WHICH) {
    const instance = draft.state[which];
    if (instance === null) continue;
    const pieces = instance.pieces.map((p) => (p.instanceId === id || p.infinite?.infiniteInstanceId === id ? change(p) : p));
    draft.state = withInstance(draft.state, which, { ...instance, pieces });
  }
}

function pieceEventKey(found: Extract<Found, { kind: 'piece' }>): string {
  return found.piece.infinite?.infiniteInstanceId ?? found.piece.instanceId;
}

/**
 * A device reported that a part or piece started (`time`: when it did). A
 * start of the next part while an autoNext is pending is the hand-over
 * itself: the auto take runs first (Sofie takes on the next part's playback
 * callback too).
 */
export function playbackStarted(draft: Draft, id: string, time: number): Rejection | null {
  const { next, autoNext } = draft.state;
  if (next?.instanceId === id && autoNext !== null) {
    const rejected = runAutoNext(draft);
    if (rejected !== null) return rejected;
  }
  const found = find(draft, id);
  if (found === null) return reject('instance-not-found', `no part or piece instance ${id} in the engine state`, null);
  if (found.kind === 'part') {
    const timings = { ...found.instance.timings, reportedStartedPlayback: found.instance.timings.reportedStartedPlayback ?? time };
    draft.state = withInstance(draft.state, found.which, { ...found.instance, timings });
    emitOnce(draft, { type: 'part-started', id: `part-started:${id}`, time, partInstanceId: id });
    return null;
  }
  replacePieces(draft, id, (p) => ({ ...p, reportedStartedPlayback: p.reportedStartedPlayback ?? time }));
  emitOnce(draft, {
    type: 'piece-started',
    id: `piece-started:${pieceEventKey(found)}`,
    time,
    pieceInstanceId: found.piece.instanceId,
    partInstanceId: found.instance.instanceId,
    source: 'reported',
  });
  return null;
}

/** A device reported that a part or piece stopped. The as-played record of a stopped previous part is corrected. */
export function playbackStopped(draft: Draft, id: string, time: number): Rejection | null {
  const found = find(draft, id);
  if (found === null) return reject('instance-not-found', `no part or piece instance ${id} in the engine state`, null);
  if (found.kind === 'part') {
    const timings = { ...found.instance.timings, reportedStoppedPlayback: time };
    draft.state = {
      ...withInstance(draft.state, found.which, { ...found.instance, timings }),
      history: draft.state.history.map((h) => (h.partInstanceId === id ? { ...h, stoppedAt: Math.max(h.startedAt, time) } : h)),
    };
    emitOnce(draft, { type: 'part-stopped', id: `part-stopped:${id}`, time, partInstanceId: id });
    return null;
  }
  replacePieces(draft, id, (p) => ({ ...p, reportedStoppedPlayback: time }));
  emitOnce(draft, {
    type: 'piece-stopped',
    id: `piece-stopped:${pieceEventKey(found)}`,
    time,
    pieceInstanceId: found.piece.instanceId,
    partInstanceId: found.instance.instanceId,
    source: 'reported',
  });
  return null;
}
